// shared/infrastructure/compresorImagen.js  (compresión de imágenes con Canvas del navegador; inyectable en los hooks)
import { PERFIL_IMAGEN, calcularDimensiones, intentosCompresion, necesitaCompresion, nombreJpg } from '../domain/compresion';

const cargarConImg = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ source: img, width: img.naturalWidth, height: img.naturalHeight, cerrar: () => {} });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo decodificar la imagen'));
    };
    img.src = url;
  });

// createImageBitmap aplica la orientación EXIF; si no existe o falla, se usa un <img> (los navegadores modernos también la aplican).
const decodificar = async file => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, cerrar: () => bitmap.close && bitmap.close() };
    } catch (e) {
      // cae al <img>
    }
  }
  return cargarConImg(file);
};

const aBlobJpeg = (canvas, calidad) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('No se pudo generar el JPEG'))), 'image/jpeg', calidad);
  });

const dibujar = (decodificada, lado) => {
  const { width, height } = calcularDimensiones(decodificada.width, decodificada.height, lado);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // aplana la transparencia del PNG
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(decodificada.source, 0, 0, width, height);
  return canvas;
};

export const compresorImagen = {
  /** Devuelve { file, comprimido, width, height }. Lanza si no se puede decodificar o generar el JPEG. */
  async comprimir(file) {
    const decodificada = await decodificar(file);
    try {
      const { width, height } = decodificada;
      if (!necesitaCompresion({ size: file.size, width, height })) return { file, comprimido: false, width, height };

      let blob = null;
      let canvas = null;
      let ladoCanvas = null;
      // eslint-disable-next-line no-restricted-syntax
      for (const { lado, q } of intentosCompresion()) {
        if (lado !== ladoCanvas) {
          canvas = dibujar(decodificada, lado);
          ladoCanvas = lado;
        }
        // eslint-disable-next-line no-await-in-loop
        blob = await aBlobJpeg(canvas, q);
        if (blob.size <= PERFIL_IMAGEN.topeBytes) break;
      }
      const salida = new File([blob], nombreJpg(file.name), { type: 'image/jpeg', lastModified: Date.now() });
      return { file: salida, comprimido: true, width: canvas.width, height: canvas.height };
    } finally {
      decodificada.cerrar();
    }
  },
};
